import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {Box, Button, Flex, HStack, Text} from '@chakra-ui/react';
import {useTranslation} from 'react-i18next';
import {format, parseISO} from 'date-fns';
import {useCallback, useState} from 'react';
import {
  calculateMonthDifference,
  formatDateForDisplay,
  getDefaultMonthOffset,
} from 'ff-common';
import {CalendarModale} from '../CalendarModale/CalendarModale';
import {BlueButton} from '@/components/Button/BlueButton';
import {CalendarHeader} from '../CalendarModale/Calendar/CalendarHeader';
import {DateRangeGrid} from './DateRangeGrid';
import {Periode} from '@/domains/liste/components/ProduitsParSaison';
import {GrayButton} from '@/components/Button/GrayButton';

/**
 * Configuration pour la modale de sélection de plage de dates
 */
export interface ConfigurationDateRangeModale {
  /** Permettre la validation sans sélection de dates */
  permettreSelectionVide?: boolean;
  /** Utiliser le mois actuel par défaut au lieu de l'année précédente */
  utiliserMoisActuelParDéfaut?: boolean;
}

// Modale de sélection d'une plage de dates
export const DateRangeModale = NiceModal.create(
  ({
    onDateRangeSelect,
    initialDateRange,
    config = {},
  }: {
    onDateRangeSelect: (periode: Periode | null) => void;
    initialDateRange?: Periode | null;
    config?: ConfigurationDateRangeModale;
  }) => {
    const modal = useModal();
    const {t} = useTranslation();
    // Utilise la fonction utilitaire pour obtenir le décalage de mois initial
    const getInitialMonthOffset = useCallback((): number => {
      if (initialDateRange?.date_debut) {
        return calculateMonthDifference(initialDateRange.date_debut);
      }

      // Utiliser le mois actuel ou l'année précédente selon la configuration
      if (config.utiliserMoisActuelParDéfaut) {
        return 0; // Mois actuel
      }

      // Sinon afficher le même mois de l'année dernière par défaut
      return getDefaultMonthOffset();
    }, [initialDateRange, config.utiliserMoisActuelParDéfaut]);

    const [monthOffset, setMonthOffset] = useState<number>(
      getInitialMonthOffset(),
    );
    const [startDate, setStartDate] = useState<Date | null>(
      initialDateRange?.date_debut
        ? parseISO(initialDateRange.date_debut)
        : null,
    );
    const [endDate, setEndDate] = useState<Date | null>(
      initialDateRange?.date_fin ? parseISO(initialDateRange.date_fin) : null,
    );
    const [validating, setValidating] = useState(false);

    // Gestion de la sélection des dates
    const handleDateClick = useCallback(
      (date: Date) => {
        if (!startDate || endDate) {
          // Si pas de date de début ou les deux dates sont déjà définies, on réinitialise
          setStartDate(date);
          setEndDate(null);
        } else {
          // Si la date cliquée est avant la date de début, on inverse
          if (date < startDate) {
            setEndDate(startDate);
            setStartDate(date);
          } else {
            setEndDate(date);
          }
        }
      },
      [startDate, endDate],
    );

    // Validation de la sélection
    const validateSelection = useCallback(() => {
      const {permettreSelectionVide = false} = config;

      // Vérifier si la sélection est valide selon la config
      const selectionValide = permettreSelectionVide
        ? !(!startDate != !endDate) //Equivalent à un XOR
        : startDate && endDate;
      if (selectionValide) {
        setValidating(true);

        let periode: Periode | null = null;

        if (startDate && endDate) {
          periode = {
            date_debut: format(startDate, 'yyyy-MM-dd'),
            date_fin: format(endDate, 'yyyy-MM-dd'),
          };
        } else if (startDate) {
          // Si seulement date de début, utiliser la même date pour la fin
          periode = {
            date_debut: format(startDate, 'yyyy-MM-dd'),
            date_fin: format(startDate, 'yyyy-MM-dd'),
          };
        } else if (endDate) {
          // Si seulement date de fin, utiliser la même date pour le début
          periode = {
            date_debut: format(endDate, 'yyyy-MM-dd'),
            date_fin: format(endDate, 'yyyy-MM-dd'),
          };
        }

        onDateRangeSelect(periode);
        modal.remove();
      }
    }, [startDate, endDate, config, onDateRangeSelect, modal]);

    // Annulation et fermeture de la modale
    const handleCancel = useCallback(() => {
      modal.remove();
    }, [modal]);

    // Initialisation de la date référence (utilisée pour l'affichage du mois)
    const referenceDate = new Date();

    return (
      <CalendarModale
        modal={modal}
        messageKey='selection_periode'
        onCancel={handleCancel}
        size_desktop='xl'
      >
        <Box>
          {/* Affichage de la sélection actuelle */}
          <Box
            bg='gray.75'
            p={4}
            borderRadius='md'
            mb={4}
          >
            <Flex
              justify='space-between'
              align='center'
            >
              <Box>
                <Text>
                  {formatDateForDisplay(
                    startDate,
                    'dd MMM yyy',
                    t('liste.date_range.debut'),
                  )}
                  {' - '}
                  {formatDateForDisplay(
                    endDate,
                    'dd MMM yyy',
                    t('liste.date_range.fin'),
                  )}
                </Text>
              </Box>
              <Button
                variant='outline'
                size='sm'
                isDisabled={!startDate}
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                }}
              >
                {t('liste.date_range.effacer')}
              </Button>
            </Flex>
          </Box>

          {/* En-tête du calendrier */}
          <CalendarHeader
            dateLivraison={referenceDate.toISOString()}
            monthOffset={monthOffset}
            setMonthOffset={setMonthOffset}
          />

          {/* Grille du calendrier adaptée pour la sélection de plage */}
          <DateRangeGrid
            onDateClick={handleDateClick}
            monthOffset={monthOffset}
            startDate={startDate}
            endDate={endDate}
            referenceDate={referenceDate}
          />

          {/* Footer avec boutons */}
          <HStack
            mt={{base: '5', md: '10'}}
            gap='3'
            flexDirection={{base: 'column', md: 'row'}}
            alignItems='stretch'
            p='5px'
          >
            <GrayButton
              variant='outline'
              onClick={handleCancel}
              flex='1'
              mt='0'
              texte={t('global.annuler')}
            />
            <BlueButton
              texte={t('liste.date_range.valider')}
              isDisabled={
                validating ||
                (() => {
                  if (config.permettreSelectionVide) {
                    // Invalide si une seule date est définie, mais permettre la sélection vide
                    return !startDate != !endDate;
                  }

                  // Si la sélection vide n'est pas permise, on exige les deux dates
                  return !startDate || !endDate;
                })()
              }
              onClick={validateSelection}
              flex='1'
              p='3'
              flexShrink={0}
              mt='0'
            />
          </HStack>
        </Box>
      </CalendarModale>
    );
  },
);
